import { KeyboardPayload, WebSocketManager } from "./WebSocketManager";
import { PlayerEvents, eventEmitter } from "./EventEmitter";
import { SDL_ENUM, codeToSDLScancode, isTV } from "../utils";
import { HTMLVideoElementWrappper } from "../utils/native";
import { UserAgent, isTizen, isWebOS, is_Mac } from "@blacknut/javascript-sdk/dist";
import { logD, logE } from "@blacknut/logging/dist";
import { LOGGING_TAG } from "../utils/logging";
export declare type GamepadButtonType =
  | "A"
  | "B"
  | "X"
  | "Y"
  | "START"
  | "LB"
  | "RB"
  | "LS"
  | "RS"
  | "DPU"
  | "DPD"
  | "DPL"
  | "DPR"
  | "SELECT"
  | "BACK"
  | "CLK";

export declare type GamepadTrigger = "LT" | "RT";
export declare type GamepadAxis = "LA" | "RA";

export declare type MouseButton = "M1" | "M2" | "M3";
export interface MouseClickEvent {
  x: number;
  y: number;
  button: MouseButton;
  type: "press" | "release";
  move?: boolean;
}
export interface BNTouchEvent {
  x: number;
  y: number;
  i?: number;
  p?: number;
  type: "press" | "move" | "release";
}

export interface BNTouchEventNew extends BNTouchEvent {
  i: number;
  p: number;
}
export interface GamepadEvent {
  type: "press" | "release" | "hold";
  player: number;
}

export interface BNKeyboardEvent {
  type: "press" | "release";
  code: string;
}

export interface GamepadButtonEvent extends GamepadEvent {
  button: GamepadButtonType;
}

export interface GamepadTriggerEvent extends GamepadEvent {
  button: GamepadTrigger;
  value: number;
}

export interface GamepadAxisEvent extends GamepadEvent {
  button: GamepadAxis;
  value: number[];
}

export interface GamepadConnectEvent {
  id?: string;
  index: number;
  hapticFeedbackCapabilities?: string[];
}

export declare type TouchPoint = {
  x: number;
  y: number;
  p?: number;
};

export interface InputProvider {
  onGamepadConnect: (e: GamepadConnectEvent) => boolean;
  onGamepadDisconnect: (e: GamepadConnectEvent) => void;
  onGamepadButtonEvent: (e: GamepadButtonEvent) => void;
  onGamepadBackEvent: (e: GamepadButtonEvent) => void;
  onGamepadTrigger: (e: GamepadTriggerEvent) => void;
  onGamepadAxis: (e: GamepadAxisEvent) => void;
  onMouseClick: (e: MouseClickEvent) => void;
  onKeyboardEvent: (e: BNKeyboardEvent) => void;
  onTouchEvent: (e: BNTouchEvent | BNTouchEventNew) => void;
  pause: () => void;
  resume: () => void;
}

export declare type MouseInputMode = "absolute" | "relative";

export class InputListener implements InputProvider {
  webSocketManager: WebSocketManager;
  keyUpCb: (e: KeyboardEvent) => void;
  keyDownCb: (e: KeyboardEvent) => void;
  notPropagatingInputs: boolean;

  backTimeout: NodeJS.Timeout;

  buttons = {
    A: "A___",
    B: "B___",
    X: "X___",
    Y: "Y___",
    START: "STRT",
    SELECT: "SLCT",

    LB: "LB__",
    RB: "RB__",
    LS: "L3__",
    RS: "R3__",
    DPU: "UP__",
    DPD: "DOWN",
    DPL: "LEFT",
    DPR: "RGHT",
    CLK: "CLK_",
  };
  axis = {
    LA: "LT",
    RA: "RT",
  };

  sdl = {
    "!": 33,
    '"': 34,
    "#": 35,
    "'": 39,
    ",": 58,
    ";": 59,
    "<": 60,
    "=": 61,
    ">": 62,
    "{": 91,
    "\\": 92,
    "}": 93,
    _: 95,
    "`": 96,
    a: 97,
    b: 98,
    c: 99,
    d: 100,
    e: 101,
    f: 102,
    g: 103,
    h: 104,
    i: 105,
    j: 106,
    k: 107,
    l: 108,
    m: 109,
    n: 110,
    o: 111,
    p: 112,
    q: 113,
    r: 114,
    s: 115,
    t: 116,
    u: 117,
    v: 118,
    w: 119,
    x: 120,
    y: 121,
    z: 122,

    Delete: 127,
    Digit9: "^",
    Digit7: 96,
    CapsLock: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_CAPSLOCK),
    F1: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_F1),
    F2: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_F2),
    F3: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_F3),
    F4: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_F4),
    F5: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_F5),
    F6: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_F6),
    F7: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_F7),
    F8: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_F8),
    F9: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_F9),
    F10: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_F10),
    F11: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_F11),
    F12: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_F12),

    Print: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_PRINTSCREEN),
    ScrollLock: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_SCROLLLOCK),
    Pause: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_PAUSE),
    Insert: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_INSERT),
    Home: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_HOME),
    PageUp: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_PAGEUP),
    End: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_END),
    PageDown: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_PAGEDOWN),
    ArrowRight: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_RIGHT),
    ArrowLeft: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_LEFT),
    ArrowDown: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_DOWN),
    ArrowUp: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_UP),

    ControlLeft: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_LCTRL),
    ShiftLeft: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_LSHIFT),
    Alt: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_LALT),
    MetaLeft: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_LGUI),
    ControlRight: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_RCTRL),
    ShiftRight: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_RSHIFT),
    AltGraph: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_RALT),
    MetaRight: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_RGUI),

    NumLock: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_NUMLOCKCLEAR),
    NumpadDivide: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_KP_DIVIDE),
    NumpadMultiply: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_KP_MULTIPLY),
    NumpadSubtract: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_KP_MINUS),
    NumpadAdd: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_KP_PLUS),
    NumpadEnter: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_KP_ENTER),
    Numpad1: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_KP_1),
    Numpad2: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_KP_2),
    Numpad3: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_KP_3),
    Numpad4: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_KP_4),
    Numpad5: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_KP_5),
    Numpad6: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_KP_6),
    Numpad7: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_KP_7),
    Numpad8: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_KP_8),
    Numpad9: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_KP_9),
    Numpad0: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_KP_0),
    NumpadDecimal: this.sdlCodeTokeycode(SDL_ENUM.SDL_SCANCODE_KP_PERIOD),

    SDLK_UNKNOWN: 0,
  };
  sdlTizen = {
    13: "R_OK__",
    10009: "R_BACK",
    39: "R_RGHT",
    37: "R_LEFT",
    40: "R_DOWN",
    38: "R_UP__",
  };

  sdlWebOS = {
    461: "R_BACK",
    13: "R_OK__",
    39: "R_RGHT",
    37: "R_LEFT",
    40: "R_DOWN",
    38: "R_UP__",
  };
  lastTouchPoints: Map<number, TouchPoint>;

  sdlDefautTV = {
    8: "R_BACK",
    13: "R_OK__",
    39: "R_RGHT",
    37: "R_LEFT",
    40: "R_DOWN",
    38: "R_UP__",
  };

  private _userAgent: UserAgent | undefined;
  public get userAgent(): UserAgent | undefined {
    return this._userAgent;
  }
  public set userAgent(v: UserAgent | undefined) {
    this._userAgent = v;
  }

  private _mouseInputMode: MouseInputMode = "absolute";
  public get mouseInputMode(): MouseInputMode {
    return this._mouseInputMode;
  }
  public set mouseInputMode(v: MouseInputMode) {
    this._mouseInputMode = v;
  }

  constructor(webSocketManager: WebSocketManager) {
    this.webSocketManager = webSocketManager;
    this.keyUpCb = this._handleKey.bind(this, 1);
    this.keyDownCb = this._handleKey.bind(this, 0);
  }
  pause() {
    this.notPropagatingInputs = true;
  }

  resume() {
    this.notPropagatingInputs = false;
  }

  private onContextMenu = (e: Event) => {
    e.preventDefault();
    return false;
  };

  init() {
    this.initKeyBoard();
    window.addEventListener("contextmenu", this.onContextMenu);

    // this.initGamepadManager();
  }
  delete() {
    this.deleteKeyBoard();
    window.removeEventListener("contextmenu", this.onContextMenu);
    // this.deleteGamepadManager();
  }

  initKeyBoard() {
    document.addEventListener("keyup", this.keyUpCb);
    document.addEventListener("keydown", this.keyDownCb);
  }

  deleteKeyBoard() {
    document.removeEventListener("keyup", this.keyUpCb);
    document.removeEventListener("keydown", this.keyDownCb);
  }

  stopInputsPropagation() {
    this.notPropagatingInputs = true;
  }
  startInputsPropagation() {
    this.notPropagatingInputs = false;
  }

  private _element: HTMLVideoElement | HTMLVideoElementWrappper | undefined;
  public get element(): HTMLVideoElement | HTMLVideoElementWrappper | undefined {
    return this._element;
  }
  public set element(v: HTMLVideoElement | HTMLVideoElementWrappper | undefined) {
    this._element = v;
  }

  public mouseInit() {
    if (!this._element) return;

    this.element.addEventListener("mousedown", (e: MouseEvent) => {
      if (this.notPropagatingInputs) return;
      const button = e.button === 1 ? 3 : e.button === 2 ? 2 : e.button + 1;
      this.sendClickEvent({
        button: `M${button}` as MouseButton,
        type: "press",
      });
    });

    this.element.addEventListener("mouseup", (e: MouseEvent) => {
      if (this.notPropagatingInputs) return;
      const button = e.button === 1 ? 3 : e.button === 2 ? 2 : e.button + 1;
      this.sendClickEvent({
        button: `M${button}` as MouseButton,
        type: "release",
      });
    });

    this.element.addEventListener("wheel", (e: WheelEvent) => {
      if (this.notPropagatingInputs) return;
      let delta;
      // natural scrolling (mac): acts like the finger is pulling/pushing
      // the presentation
      // inverted scrolling (win): acts like the finger is pulling/pushing
      // the scroller aside the page
      // depends on the os where it runs, the navigator will send different
      // values for the same movement: do not try to normalize so as to get
      // the os scroll reflected in-game, e.g. natural on mac, reverse on win
      if (e.deltaY > 0) {
        delta = 1;
      } else if (e.deltaY < 0) {
        delta = 0;
      } else {
        return;
      }
      const data = `MW|${delta};`;
      this.webSocketManager.sendDataMouse(data);
    });

    this.element.addEventListener("mousemove", (event: MouseEvent) => {
      if (this.notPropagatingInputs) return;
      const bbox = this.element.getBoundingClientRect();
      const w = bbox.width;
      const h = bbox.height;
      if (w === 0 || h === 0) {
        logE(LOGGING_TAG, "Invalid video size : %o*%o", w, h);
        return;
      }
      const scaleX = this.element.videoWidth / w;
      const scaleY = this.element.videoHeight / h;
      // logD(LOGGING_TAG,
      //   "Video size %o*%o, offset :%o*%o, scale: %o*%o %o",
      //   this.element.videoWidth,
      //   this.element.videoHeight,
      //   w,
      //   h,
      //   scaleX,
      //   scaleY,
      //   event.movementX
      // );
      if (this.mouseInputMode === "relative") {
        const x =
          Math.sign(event.movementX) * Math.ceil(Math.abs(event.movementX * scaleX));
        const y =
          Math.sign(event.movementY) * Math.ceil(Math.abs(event.movementY * scaleY));

        // logD(LOGGING_TAG,
        //   "mousemove event rel %o %o => %o %o",
        //   event.movementX,
        //   event.movementY,
        //   x,
        //   y,
        // );

        if (x != 0 || y != 0) {
          this.webSocketManager.sendDataMouse(`xpos:${x}|ypos:${y};`);
        }
      } else {
        const x = Math.ceil((event.clientX - bbox.left) * scaleX + 0.5); // round to integer
        const y = Math.ceil((event.clientY - bbox.top) * scaleY + 0.5);
        this.webSocketManager.sendDataMouse(`xref:${x}|yref:${y};`);
        // logD(LOGGING_TAG,
        //   "mousemove event abs %o %o => %o %o",
        //   event.clientX,
        //   event.clientY,
        //   x,
        //   y
        // );
      }
    });
  }

  public touchInit() {
    if (!this._element) {
      console.error("touchInit no element");
      return;
    }
    this.lastTouchPoints = new Map<number, TouchPoint>();
    this.element.addEventListener("touchstart", (event: TouchEvent) => {
      for (let i = 0; i < event.changedTouches.length; i++) {
        this.onTouchEvent({
          type: "press",
          x: event.changedTouches.item(i).clientX,
          y: event.changedTouches.item(i).clientY,
          i: event.changedTouches.item(i).identifier,
          p: event.changedTouches.item(i).force,
        });
      }
      event.stopPropagation();
    });
    this.element.addEventListener("touchend", (event: TouchEvent) => {
      for (let i = 0; i < event.changedTouches.length; i++) {
        this.onTouchEvent({
          type: "release",
          x: event.changedTouches.item(i).clientX,
          y: event.changedTouches.item(i).clientY,
          i: event.changedTouches.item(i).identifier,
          p: event.changedTouches.item(i).force,
        });
      }
      event.stopPropagation();
    });
    this.element.addEventListener("touchcancel", (event: TouchEvent) => {
      for (let i = 0; i < event.changedTouches.length; i++) {
        this.onTouchEvent({
          type: "release",
          x: event.changedTouches.item(i).clientX,
          y: event.changedTouches.item(i).clientY,
          i: event.changedTouches.item(i).identifier,
          p: event.changedTouches.item(i).force,
        });
      }
      event.stopPropagation();
    });
    this.element.addEventListener("touchmove", (event: TouchEvent) => {
      for (let i = 0; i < event.changedTouches.length; i++) {
        this.onTouchEvent({
          type: "move",
          x: event.changedTouches.item(i).clientX,
          y: event.changedTouches.item(i).clientY,
          i: event.changedTouches.item(i).identifier,
          p: event.changedTouches.item(i).force,
        });
      }
      event.stopPropagation();
    });
    // seems in addition we should install a poll during touch sequence
    // to update the force as no event is raised if only force/pressure
    // changes
  }

  onGamepadConnect(e: GamepadConnectEvent) {
    eventEmitter.emit(PlayerEvents.GAMEPAD_CONNECT, e);
    return this.webSocketManager.createGamepadSocket(e.index, {
      gamepad: { id: e.id, hapticCaps: e.hapticFeedbackCapabilities },
    });
  }
  onGamepadDisconnect(e: GamepadConnectEvent) {
    eventEmitter.emit(PlayerEvents.GAMEPAD_DISCONNECT, e);
    this.webSocketManager.closeGamepadSocket(e.index);
  }

  onGamepadBackEvent(e: GamepadButtonEvent) {
    // if (e.player === 0) {
    switch (e.type) {
      case "press":
        this.backTimeout = setTimeout(() => {
          eventEmitter.emit(PlayerEvents.LONG_BACK);
          clearTimeout(this.backTimeout);
        }, 2000);
        break;
      case "release":
        clearTimeout(this.backTimeout);
        break;
    }
    // }

    const suffix = e.type == "release" ? 1 : 0;
    const data = `BACK|${suffix};`;
    this.handleGamePadEvent(e, data);
  }

  onGamepadButtonEvent(e: GamepadButtonEvent) {
    const suffix = e.type == "release" ? 1 : 0;
    const data = `${this.buttons[e.button]}|${suffix};`;
    this.handleGamePadEvent(e, data);
  }
  onGamepadTrigger(e: GamepadTriggerEvent) {
    let value = -1;
    if (e.type !== "release") {
      value = this.boundary(e.value * 2 - 1); // Stream server expects value between [-1,1]
    }
    const data = `${e.button}_Z|${value};`;
    this.handleGamePadEvent(e, data);
  }

  onGamepadAxis(e: GamepadAxisEvent) {
    const prefix = this.axis[e.button];
    let x = 0,
      y = 0;

    if (e.type !== "release") {
      x = this.boundary(e.value[0]);
      y = this.boundary(e.value[1]);
    }

    const data = `${prefix}_X|${x};${prefix}_Y|${y};`;
    this.handleGamePadEvent(e, data);
  }

  boundary(val: number) {
    // force value to be between -1 and 1 and round to 2 decimal
    return Math.round((Math.min(Math.max(val, -1), 1) + 0.00001) * 100) / 100;
  }

  // initGamepadManager() {
  //   if (this.gamepad) {
  //     this.gamepad.on('connect', (e) => this.onGamepadConnect(e));
  //     this.gamepad.on('disconnect', (e) => this.onGamepadDisconnect(e));
  //     this.gamepad.on('press hold release', Object.keys(this.buttons), (e) => this.onGamepadButtonEvent(e));
  //     this.gamepad.on('press hold release', 'BACK', (e) => this.onGamepadBackEvent(e));

  //     // Analog Axis
  //     this.gamepad.on('press hold release', ['LT', 'RT'], (e) => this.onGamepadTrigger(e));
  //     this.gamepad.on('press hold release', Object.keys(this.axis), (e) => this.onGamepadAxis(e));
  //   }
  // }

  handleGamePadEvent(e: GamepadEvent, data: string) {
    if (this.notPropagatingInputs) return;
    eventEmitter.emit(PlayerEvents.GAMEPAD_EVENT, data);
    this.webSocketManager.sendDataGamepad(e.player, data);
  }

  private isTouchEventNew = (
    e: BNTouchEvent | BNTouchEventNew,
  ): e is BNTouchEventNew => {
    return e.i !== undefined;
  };

  public onTouchEvent(e: BNTouchEvent | BNTouchEventNew) {
    if (this.isTouchEventNew(e)) {
      this.onTouchEventNew(e);
    } else {
      this.onTouchEventOld(e);
    }
  }

  private onTouchEventOld(e: BNTouchEvent) {
    if (!this._element) return;

    const bbox = this._element.getBoundingClientRect(); // absolute geometrics of element
    const scaleX = this._element.videoWidth / bbox.width;
    const scaleY = this._element.videoHeight / bbox.height;
    /*
    console.log("e ", e);
    console.log("video", this._element.videoWidth, this._element.videoHeight);

    console.log("bbox", bbox.left, bbox.right);
    console.log("scale", scaleX, scaleY);
*/
    const x = Math.ceil((e.x - bbox.left) * scaleX + 0.5); // round to integer
    const y = Math.ceil((e.y - bbox.top) * scaleY + 0.5);
    this.webSocketManager.sendDataTouch(
      `TX:${x}|TY:${y};TO|${e.type === "press" ? 0 : 1}`,
    );
  }

  private onTouchEventNew(e: BNTouchEvent) {
    if (!this._element) return;

    const bbox = this._element.getBoundingClientRect(); // absolute geometrics of element
    const scaleX = this._element.videoWidth / bbox.width;
    const scaleY = this._element.videoHeight / bbox.height;
    /*
    console.log("e ", e);
    console.log("video", this._element.videoWidth, this._element.videoHeight);

    console.log("bbox", bbox.left, bbox.right);
    console.log("scale", scaleX, scaleY);
*/
    const x = Math.ceil((e.x - bbox.left) * scaleX + 0.5); // round to integer
    const y = Math.ceil((e.y - bbox.top) * scaleY + 0.5);
    const p = Math.ceil(e.p * 100);

    // for some reason Huawei P40 I'm working with does not respect the max value
    // for force. Probably we will remove TP below as it does not work on target
    // linux. It is optional anyway protocol wise

    // build input command with coordinatess
    let cmd = `TX:${x}|TY:${y}|TP:${p}|TI:${e.i};`;
    // eventually add start/press end/release mark
    if (e.type === "press") {
      this.lastTouchPoints[e.i] = { x: x, y: y };
      cmd += `TO|0|TI:${e.i};`;
    } else if (e.type === "release") {
      this.lastTouchPoints.delete(e.i);
      cmd += `TO|1|TI:${e.i};`;
    } else if (e.type === "move") {
      let lastPoint: TouchPoint | undefined;
      if (undefined !== (lastPoint = this.lastTouchPoints[e.i])) {
        if (lastPoint.x === x && lastPoint.y === y && lastPoint.p === p) {
          return;
        }
      }
    }
    this.lastTouchPoints[e.i] = { x, y, p };
    this.webSocketManager.sendDataTouch(cmd);
  }

  public onMouseClick(e: MouseClickEvent) {
    if (!this._element) return;

    const { move = true } = e;
    if (move) {
      const bbox = this._element.getBoundingClientRect(); // absolute geometrics of element
      const scaleX = this._element.videoWidth / bbox.width;
      const scaleY = this._element.videoHeight / bbox.height;

      // console.log("bbox", bbox.left, bbox.right);
      // console.log("scale", scaleX, scaleY);

      const x = Math.ceil((e.x - bbox.left) * scaleX + 0.5); // round to integer
      const y = Math.ceil((e.y - bbox.top) * scaleY + 0.5);
      this.webSocketManager.sendDataMouse(`xref:${x}|yref:${y};`);
    }
    this.sendClickEvent(e);
  }

  public sendClickEvent({
    button,
    type,
  }: {
    button: MouseButton;
    type: "press" | "release";
  }) {
    this.webSocketManager.sendDataMouse(`${button}|${type === "press" ? 0 : 1};`);
  }

  private _handleKey(value: number, e: KeyboardEvent) {
    if (this.notPropagatingInputs) return;
    e.preventDefault();
    let charCode: string;
    if (isTizen()) {
      charCode = this.getSdlCodeFromTizenEvent(e);
      charCode && this.webSocketManager.sendDataRemote(`${charCode}|${value};`);
    } else if (isWebOS()) {
      charCode = this.getSdlCodeFromWebOSEvent(e);
      charCode && this.webSocketManager.sendDataRemote(`${charCode}|${value};`);
    } else if (this.userAgent && isTV(this.userAgent)) {
      charCode = this.getRemoteSdlCodeFromEvent(e);
      charCode && this.webSocketManager.sendDataRemote(`${charCode}|${value};`);
    }

    if (!charCode) {
      charCode = this.getSdlCodeFromEvent(e);
      if (charCode.length == 1) {
        charCode = `0${charCode}`;
      }
      // new format extension, add scan code sc and printable representation pr
      let payload: KeyboardPayload | undefined = undefined;
      if ((e.code) && (0 != e.code.length)) {
        payload = {
          event: {
            sc: codeToSDLScancode[e.code],
          },
        };
        if (1 == e.key.length) {
          payload.event["pr"] = e.key.charCodeAt(0).toString(16).padStart(4, "0");
        }
      }
      // on macs, events are sent according to the toggle change for toggle
      // keys, rather than on the key press release event.
      // so send a fake key press + release event to align with other platforms
      if (
        is_Mac() &&
        (e.code == "CapsLock" || e.code == "NumLock" || e.code == "ScrollLock")
      ) {
        this.webSocketManager.sendDataKeyboard(`${charCode}|0;`, payload);
        this.webSocketManager.sendDataKeyboard(`${charCode}|1;`, payload);
      } else {
        this.webSocketManager.sendDataKeyboard(`${charCode}|${value};`, payload);
      }
    }
    if (value) {
      this.sendKeyToClient(e);
    }
  }

  public onKeyboardEvent(e: BNKeyboardEvent) {
    this.webSocketManager.sendDataKeyboard(`${e.code}|${e.type === "press" ? 0 : 1};`);
  }

  sendKeyToClient(e) {
    const charcode = e.keyCode || e.which;

    if (isTizen()) {
      switch (charcode) {
        case 10009:
          eventEmitter.emit(PlayerEvents.BACK);
          break;
        case 10182:
          eventEmitter.emit(PlayerEvents.LONG_BACK);
          break;
      }
    } else {
      switch (charcode) {
        case 27:
          if (e.repeat) {
            eventEmitter.emit(PlayerEvents.LONG_BACK);
          } else {
            eventEmitter.emit(PlayerEvents.BACK);
          }
          break;
      }
    }
  }

  public injectEscapeSequence() {
    // inject escape key down/up to the game
    logD(LOGGING_TAG, "injecting escape sequence to game");
    this.webSocketManager.sendDataKeyboard("1b|0", {
      event: { sc: codeToSDLScancode["Escape"] },
    });
    setTimeout(() => {
      this.webSocketManager.sendDataKeyboard("1b|1", {
        event: { sc: codeToSDLScancode["Escape"] },
      });
    }, 100);
  }

  /**
   *
   * SDL Code
   *
   */
  getSdlCodeFromEvent(e: KeyboardEvent) {
    let key = e.key;
    let sdlcode = e.keyCode || e.which;

    if (e.location === 3 || ["Shift", "Control", "Dead", "Meta"].indexOf(key) > -1) {
      key = e.code;
    }
    if (this.sdl[key]) {
      sdlcode = this.sdl[key];
    }
    return sdlcode.toString(16);
  }

  getSdlCodeFromTizenEvent(e: KeyboardEvent) {
    const key = e.keyCode || e.which;
    // logD(LOGGING_TAG,
    //   "[Tizen] Key event :%o %o %o : %o",
    //   e.keyCode,
    //   e.which,
    //   e.key,
    //   this.sdlTizen[key],
    // );

    if (this.sdlTizen[key]) {
      return this.sdlTizen[key];
    }
    return undefined;
  }

  getSdlCodeFromWebOSEvent(e: KeyboardEvent) {
    const key = e.keyCode || e.which;
    //  logD(LOGGING_TAG, "[WebOS] Key event :%o %o %o", e.keyCode, e.which, e.key);
    if (this.sdlWebOS[key]) {
      const res = this.sdlWebOS[key];
      return res;
    }

    return undefined;
  }

  getRemoteSdlCodeFromEvent(e: KeyboardEvent) {
    const key = e.keyCode || e.which;
    //  logD(LOGGING_TAG, "[WebOS] Key event :%o %o %o", e.keyCode, e.which, e.key);
    if (this.sdlDefautTV[key]) {
      const res = this.sdlDefautTV[key];
      return res;
    }

    return undefined;
  }

  sdlCodeTokeycode(x) {
    const mask = 1 << 30;
    return x | mask;
  }
}
